import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Icon, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, DialogActions, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WDBox from "../../../../../MaterialComponents/WDBox";
import WDButton from "../../../../../MaterialComponents/WDButton";
import pxToRem from "../../../../../Themes/Functions/pxToRem";
import { STAT_FIELDS, DOCUMENT_TYPE_MAPPING } from "../../../../../Configurations/StatFieldData";
import apiService from "../../../../../services/api";
import { useDispatch } from "react-redux";
import { showLoader, dismissLoader } from "../../../../../LayoutContainers/AppLayout/redux/AppSlice";

const FileField = ({ field, onFileUpload, index, onFileRemove, isProcessed, employeeUid, selectedEmployee, contextProps, deferBulkUpload = false, allowMultiple = false }) => {
  const dispatch = useDispatch();
  // Use context props passed from parent
  const { currentContext, shouldShowFieldInSection, isDocumentField } = contextProps || {};
  
  // State for document viewing modal
  const [openModal, setOpenModal] = useState(false);
  const [documentData, setDocumentData] = useState(null);
  const [documentTitle, setDocumentTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [viewError, setViewError] = useState(null);
  
  // Debug log for props
  console.log('%cFileField props:', 'color: purple; font-weight: bold', {
    fieldName: field?.name,
    fieldTitle: field?.title,
    fieldType: field?.type,
    hasEmployeeUid: !!employeeUid,
    employeeUid: employeeUid || 'none',
    hasSelectedEmployee: !!selectedEmployee,
    employeeId: selectedEmployee?.employeeId || 'none',
    hasContextProps: !!contextProps,
    index
  });
  
  // List of specific document fields that should always be visible
  const staticDocumentFields = [
    'birthCertificate',
    'passportCopy',
    'visaPage',
    'salaryCertificate',
    'photo',
    'shinasnama',
    'labourContract',
    'resume',
    'educationalCertificate',
    'experienceCertificate',
    'policeClearanceCertificate'
  ];
  
  // Check if this field should be visible in the current context
  // Always show document fields in CustomerStatFieldInfo component
  const isDocField = field && field.name && isDocumentField && isDocumentField(field.name);
  const isStaticDocField = field && field.name && staticDocumentFields.includes(field.name);
  const isVisible = isDocField || isStaticDocField || (shouldShowFieldInSection && field && field.name && shouldShowFieldInSection(field.name, 'DOCUMENTS'));
  
  const [file, setFile] = useState();
  const [isFileUploading, fileUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  
  // Handle file selection from input
  const _onFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    // Check file size (limit to 10MB each)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    const tooBig = selectedFiles.find((f) => f.size > maxSize);
    if (tooBig) {
      alert(`"${tooBig.name}" exceeds the 10MB limit. Please select a smaller file.`);
      return;
    }

    // Reset the input so selecting the same file(s) again still fires onChange
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (allowMultiple) {
      console.log(`Selected ${selectedFiles.length} file(s) for multi-file field:`, field?.name);
      uploadMultipleFiles(selectedFiles);
      return;
    }

    const selectedFile = selectedFiles[0];
    setFile(selectedFile);
    console.log('File set in state, preparing to upload:', selectedFile.name);
    uploadFile(selectedFile);
  };

  // Upload several files for a single-instance document field and accumulate them into one value.
  // Files go to S3 only (bulk write-back to the employee profile is deferred to ticket submit). The
  // value carries an `isNewUpload` flag + `documentReferences` array so the existing multi-file display
  // renders every file and the submit-time sync saves them all as one group (replacing the current
  // version). Re-selecting appends to the files already staged in this session.
  const uploadMultipleFiles = async (filesToUpload) => {
    const currentEmployeeUid = getEmployeeUid();
    if (!currentEmployeeUid) {
      alert('Cannot upload file: No employee selected. Please select an employee first.');
      return;
    }
    const documentType = getDocumentType();
    const backendDocumentType = toBackendDocumentType(documentType);

    fileUploading(true);
    dispatch(showLoader('Uploading documents...'));
    try {
      // Keep files already staged in this session (append), but not auto-filled existing refs
      const existing = (field?.value && field.value.isNewUpload && Array.isArray(field.value.documentReferences))
        ? field.value.documentReferences
        : [];
      const uploaded = [];
      for (const fileToUpload of filesToUpload) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', fileToUpload);
        uploadFormData.append('documentType', backendDocumentType);
        uploadFormData.append('employeeUid', currentEmployeeUid);
        uploadFormData.append('fileName', fileToUpload.name);
        // eslint-disable-next-line no-await-in-loop
        const uploadAxiosResp = await apiService.api.post(
          '/v1/files/upload',
          uploadFormData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        const uploadResult = uploadAxiosResp.data || {};
        const filePath = uploadResult.filePath || (uploadResult.data && uploadResult.data.filePath) ||
          uploadResult.path || (uploadResult.data && uploadResult.data.path);
        if (!filePath) {
          console.error('No filePath in upload response for', fileToUpload.name);
          continue;
        }
        uploaded.push({
          filePath,
          fileName: fileToUpload.name,
          contentType: fileToUpload.type || null,
          fileSize: fileToUpload.size || null,
        });
      }

      const allRefs = [...existing, ...uploaded].map((r, i) => ({ ...r, isPrimary: i === 0 }));
      if (allRefs.length === 0) return;
      const primary = allRefs[0];
      const value = {
        isNewUpload: true,
        documentType: backendDocumentType,
        documentGroupId: null,
        id: primary.filePath,
        fileName: primary.fileName,
        filePath: primary.filePath,
        documentReferences: allRefs,
        hasMultipleFiles: allRefs.length > 1,
        totalFiles: allRefs.length,
      };
      if (onFileUpload) onFileUpload(value, field);
    } catch (error) {
      console.error('Error during multi-file upload:', error);
      alert(`Error uploading file: ${error.message}`);
    } finally {
      dispatch(dismissLoader());
      fileUploading(false);
    }
  };
  
  // Consolidated function to handle file upload
  // Uses simplified 2-API flow: /v1/files/upload then /v1/employee-documents/{id}/bulk
  const uploadFile = async (fileToUpload) => {
    if (!fileToUpload) {
      console.error('No file provided to uploadFile');
      return;
    }
    
    console.log('Starting upload for file:', fileToUpload.name);
    fileUploading(true);
    dispatch(showLoader("Uploading document..."));
    
    try {
      // Verify employee UID is available before starting upload
      const currentEmployeeUid = getEmployeeUid();
      console.log('Employee UID for upload:', currentEmployeeUid);
      
      if (!currentEmployeeUid) {
        alert('Cannot upload file: No employee selected. Please select an employee first.');
        fileUploading(false);
        return;
      }
      
      // Get document type from field name
      const documentType = getDocumentType();
      const backendDocumentType = toBackendDocumentType(documentType);
      
      const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(field.name);
      
      // Step 1: Upload the file with documentType and employeeUid
      console.log('Step 1: Uploading file to /v1/files/upload');
      
      const uploadFormData = new FormData();
      uploadFormData.append('file', fileToUpload);
      uploadFormData.append('documentType', backendDocumentType);
      uploadFormData.append('employeeUid', currentEmployeeUid);
      uploadFormData.append('fileName', fileToUpload.name);
      
      console.log('Uploading file with FormData:', {
        fileName: fileToUpload.name,
        fileSize: fileToUpload.size,
        documentType: backendDocumentType,
        employeeUid: currentEmployeeUid
      });
      
      const uploadAxiosResp = await apiService.api.post(
        '/v1/files/upload',
        uploadFormData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const uploadResult = uploadAxiosResp.data || {};
      console.log('File upload successful:', uploadResult);
      
      // Extract filePath from upload response
      const filePath = uploadResult.filePath || 
                    (uploadResult.data && uploadResult.data.filePath) || 
                    uploadResult.path ||
                    (uploadResult.data && uploadResult.data.path);
      
      console.log('Extracted filePath:', filePath);
      
      if (!filePath) {
        throw new Error('Failed to get filePath from upload response');
      }
      
      // Step 2: Call bulk API to save document (only for static document fields)
      let documentGroupId = null;
      let bulkDocumentId = null;
      
      if (!deferBulkUpload && currentEmployeeUid && backendDocumentType && isDocumentField && isDocumentField(field.name) && !isDynamicServiceField) {
        console.log('Step 2: Calling /v1/employee-documents/{id}/bulk');
        
        const bulkPayload = {
          documentType: backendDocumentType,
          replaceExisting: false,
          documents: [{
            filePath: filePath,
            fileName: fileToUpload.name,
            fileSize: fileToUpload.size
          }]
        };

        // Multiple-instance certificate types (educational/experience) are keyed by a
        // document name (title). There is no name prompt during ticket creation, so
        // derive one from the file name to satisfy the backend's title requirement.
        const MULTI_INSTANCE_DOC_TYPES = ['educational_certificate', 'experience_certificate'];
        if (MULTI_INSTANCE_DOC_TYPES.includes(backendDocumentType)) {
          const baseName = fileToUpload.name.replace(/\.[^/.]+$/, '').trim();
          bulkPayload.title = baseName || fileToUpload.name;
          bulkPayload.isReplace = false;
        }

        console.log('Bulk API payload:', bulkPayload);
        
        try {
          const bulkResp = await apiService.api.post(
            `/v1/employee-documents/${currentEmployeeUid}/bulk`,
            bulkPayload,
            { headers: { 'Content-Type': 'application/json' } }
          );
          
          const bulkResult = bulkResp.data?.data || bulkResp.data || [];
          console.log('Bulk API response:', bulkResult);
          
          // Extract documentGroupId and id from bulk response
          if (Array.isArray(bulkResult) && bulkResult.length > 0) {
            documentGroupId = bulkResult[0].documentGroupId;
            bulkDocumentId = bulkResult[0].id;
            console.log('Extracted from bulk response:', { documentGroupId, bulkDocumentId });
          }
        } catch (bulkError) {
          console.error('Bulk API error:', bulkError);
          // Continue even if bulk fails - file is already uploaded
        }
      }
      
      // Create file metadata with documentGroupId for documentReferences
      const fileMetadata = {
        id: bulkDocumentId || filePath,
        fileName: fileToUpload.name,
        filePath: filePath,
        fileSize: fileToUpload.size,
        fileType: fileToUpload.type,
        documentType: backendDocumentType,
        documentGroupId: documentGroupId,
        uploadTime: new Date().toISOString()
      };
      
      console.log('Created fileMetadata object:', fileMetadata);

      // Call the parent's onFileUpload callback with the file metadata
      if (onFileUpload) {
        onFileUpload(fileMetadata, field);
      }
      
    } catch (error) {
      console.error('Error during file upload:', error);
      alert(`Error uploading file: ${error.message}`);
    } finally {
      dispatch(dismissLoader());
      fileUploading(false);
    }
  };
  
  // Handle file removal
  const _onFileRemove = () => {
    // Clear the file from state
    setFile(null);

    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    // Call the parent's onFileRemove callback if provided. Pass the field object first (NewTicket's
    // and EditTicket's handlers key off field.id/field.name); index second for any positional use.
    // Passing only `index` here previously broke dynamic document removal (field.name was undefined).
    if (onFileRemove) {
      onFileRemove(field, index);
    }

    console.log('File removed');
  };
  
  // Helper function to check if a field is a file field
  const isFileField = (field) => {
    // Check if field has name property and it exists in STAT_FIELDS
    if (field && field.name && STAT_FIELDS[field.name] && STAT_FIELDS[field.name].type === 'FILE') {
      return true;
    }
    
    // Check if field has title property and it's a file type
    if (field && field.type === 'PDF') {
      console.log('%cDetected PDF field type:', 'color: blue', field.title);
      return true;
    }
    
    return false;
  };
  
  // Helper function to convert camelCase to snake_case
  const camelToSnakeCase = (str) => {
    return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
  };

  // Map frontend doc types to backend expected lowercase snake_case
  const toBackendDocumentType = (t) => {
    if (!t) return null;
    switch (t) {
      case 'passportCopy': return 'passport_copy';
      case 'visaPage': return 'visa_page';
      case 'salaryCertificate': return 'salary_certificate';
      case 'birthCertificate': return 'birth_certificate';
      case 'emiratesId': return 'emirates_id';
      case 'photo': return 'photo';
      case 'shinasnama': return 'shinasnama';
      case 'labourContract': return 'labour_contract';
      default:
        // convert camelCase/PascalCase to snake_case
        return camelToSnakeCase(String(t)).toLowerCase();
    }
  };
  
  // Helper function to get employee ID from props, field, context or global variable
  const getEmployeeUid = () => {
    // First check if we have the selectedEmployee prop (highest priority)
    if (selectedEmployee) {
      if (selectedEmployee.employeeId) {
        console.log('%cUsing employeeId from selectedEmployee prop:', 'color: green', selectedEmployee.employeeId);
        return selectedEmployee.employeeId; // This is already in the correct format (EMP-XXXX)
      }
    }
    
    // Next check if employeeUid prop is already in the correct format
    if (employeeUid && employeeUid.startsWith('EMP-')) {
      console.log('%cUsing employeeUid prop (already in EMP format):', 'color: green', employeeUid);
      return employeeUid;
    }
    
    // Check other possible sources for the full employee data
    let employeeData = null;
    if (field && field.context && field.context.selectedEmployee) {
      employeeData = field.context.selectedEmployee;
    } else if (field && field.value && field.value.selectedEmployee) {
      employeeData = field.value.selectedEmployee;
    } else if (window && window.selectedEmployee) {
      employeeData = window.selectedEmployee;
    }
    
    // If we have the full employee data, use the employeeId field which is in the correct format
    if (employeeData && employeeData.employeeId) {
      console.log('%cUsing employeeId from field data:', 'color: green', employeeData.employeeId);
      return employeeData.employeeId;
    }
    
    // Fallback to checking for direct employeeId values
    const employeeIdValue = 
           (field && field.employeeId) || 
           (field && field.value && field.value.employeeId) || 
           (field && field.context && field.context.employeeId) ||
           (window && window.currentEmployeeId);
    
    if (employeeIdValue) {
      console.log('%cUsing direct employeeId value:', 'color: green', employeeIdValue);
      return employeeIdValue;
    }
    
    // Last resort: use uid values
    const uidValue = employeeUid || 
           (field && field.employeeUid) || 
           (field && field.value && field.value.employeeUid) || 
           (field && field.context && field.context.employeeUid) ||
           (window && window.currentEmployeeUid);
    
    // Enhanced logging for employee ID retrieval
    if (!uidValue && !employeeIdValue) {
      console.error('%cEMPLOYEE ID MISSING', 'color: red; font-weight: bold; font-size: 16px');
      console.error('%cField details:', 'color: red', {
        fieldName: field.name || 'unknown',
        fieldTitle: field.title || 'unknown',
        fieldType: field.type || 'unknown',
        fieldValue: field.value ? 'present' : 'missing',
        fieldContext: field.context ? 'present' : 'missing',
        propsEmployeeUid: employeeUid ? 'present' : 'missing',
        timestamp: new Date().toISOString()
      });
      return null;
    } 
    
    if (uidValue) {
      console.log('%cOnly UID found, no employeeId available:', 'color: orange', uidValue);
    }
    
    return uidValue;
  };
  
  // Helper function to get document type from field
  const getDocumentType = () => {
    if (!field) return null;
    
    // Use camelCase format for document type as expected by the API
    // Special case for salary certificate
    if (field.name === 'salaryCertificate') {
      return 'salaryCertificate';
    }
    // Check if document type is already provided in the field value
    if (field.value && field.value.documentType) {
      return field.value.documentType;
    }
    // Default to using the field name as the document type
    if (field.name) {
      return field.name;
    }

    return null;
  };

// Function to try a direct file download approach for null content type
const tryDirectFileDownload = async () => {
  try {
    setIsLoading(true);
    setViewError(null);

    // Determine context
    const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(field.name);
    const uid = getEmployeeUid();
    const documentType = getDocumentType();
    const backendDocumentType = toBackendDocumentType(documentType);

    let directUrl;
    if (field.value?.filePath) {
      // Use file path for dynamic service fields or any field with a file path
      directUrl = `/v1/files/content?filePath=${encodeURIComponent(field.value.filePath)}`;
    } else if (field.value?.id && !isDynamicServiceField) {
      // Use document ID for static fields only
      directUrl = `/v1/documents/${field.value.id}/content`;
    } else if (!isDynamicServiceField && uid && backendDocumentType) {
      // Use employee profile documents for static fields only
      directUrl = `/v1/users/${uid}/documents/${backendDocumentType}/content`;
    } else {
      // Dynamic service field without file path - cannot view
      throw new Error('Dynamic service field has no file path for content viewing');
    }

    console.log('Trying direct file content endpoint:', directUrl);
    const response = await apiService.api.get(directUrl, { responseType: 'blob' });
    const blob = response.data;

    // Create a blob URL for the modal to display
    const url = URL.createObjectURL(blob);

    // Set the document data for the modal
    setDocumentData(url);
    setIsLoading(false);

    console.log('Document loaded successfully using direct file content approach');
  } catch (error) {
    console.error('Error with direct file download:', error);
    return tryAlternativeEndpoint();
  }
};
    
    // Function to try an alternative endpoint
    const tryAlternativeEndpoint = async () => {
      try {
        setIsLoading(true);
        setViewError(null);

        // Try the alternative endpoint - only for static fields
        const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(field.name);

        if (isDynamicServiceField) {
          // Dynamic service fields should not use employee profile document endpoints
          throw new Error('Dynamic service field cannot use alternative employee document endpoint');
        }

        const uid = getEmployeeUid();
        const documentType = getDocumentType();
        const backendDocumentType = toBackendDocumentType(documentType);
        const altUrl = `/v1/users/${uid}/documents/${backendDocumentType}/download`;
        console.log('Trying alternative endpoint for static field:', altUrl);
        const response = await apiService.api.get(altUrl, { responseType: 'blob' });
        const blob = response.data;

        // Create a blob URL for the modal to display
        const url = URL.createObjectURL(blob);

        // Set the document data for the modal
        setDocumentData(url);
        setIsLoading(false);

        console.log('Document loaded successfully from alternative endpoint');
      } catch (error) {
        console.error('Error with alternative endpoint:', error);
        setViewError(error.message);
        setIsLoading(false);
      }
    };
    
    // Start loading the document
    // Removed unintended immediate invocation and stray brace that closed scope early
    // tryDirectFileDownload();
    // Note: viewing is triggered explicitly via handlers, not at definition time
  
  
  // Handle modal close
  const handleCloseModal = () => {
    setOpenModal(false);
    // Clean up any blob URLs to prevent memory leaks
    if (documentData) {
      URL.revokeObjectURL(documentData);
      setDocumentData(null);
    }
  };

  // Handle retry with alternative endpoint
  const handleRetry = () => {
    // Check if this is a dynamic service field
    const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(field.name);
    
    if (isDynamicServiceField) {
      // Dynamic service fields cannot use employee profile document endpoints
      setViewError('Dynamic service field cannot be retried with employee document endpoint. File may not have been uploaded successfully.');
      setIsLoading(false);
      return;
    }
    
    const uid = getEmployeeUid();
    const documentType = getDocumentType();
    const altUrl = `/v1/users/${uid}/documents/${documentType}/download`;
    
    setIsLoading(true);
    setViewError(null);
    
    apiService.api.get(altUrl, { responseType: 'blob' })
      .then(response => {
        const blob = response.data;
        const url = URL.createObjectURL(blob);
        setDocumentData(url);
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Error with retry:', error);
        setViewError(error.message || 'Failed to load document');
        setIsLoading(false);
      });
  };
  
  // Visibility rules will be applied right before render to avoid parser confusion

  // Documents attached to this field (auto-filled references or an uploaded set).
  const documentReferences = field?.value?.documentReferences || [];

  // Handle viewing individual documents from the list
  const handleViewDocument = (doc) => {
    setDocumentTitle(`${doc.fileName} - ${field?.title || field?.name || 'Document'}`);
    setOpenModal(true);
    setIsLoading(true);
    setViewError(null);
    setDocumentData(null);

    // Load the specific document
    const loadSpecificDocument = async () => {
      try {
        const documentUrl = `/v1/files/download?filePath=${encodeURIComponent(doc.filePath)}`;
        const response = await apiService.api.get(documentUrl, { responseType: 'blob' });
        const blob = response.data;
        const url = URL.createObjectURL(blob);
        
        setDocumentData(url);
        setIsLoading(false);
        
        console.log('Document loaded successfully:', doc.fileName);
      } catch (error) {
        console.error('Error loading document:', error);
        setViewError(error.message || 'Failed to load document');
        setIsLoading(false);
      }
    };
    
    loadSpecificDocument();
  };

  // Ensure visibility guard is inside component scope, just before render
  if (!isVisible && field?.category !== undefined) {
    console.log(`FileField ${field?.name} is not visible in context ${currentContext}`);
    return null;
  }

  // Files to show: auto-filled references (single or multiple), or a single uploaded/auto-filled file.
  const displayFiles = (() => {
    if (Array.isArray(documentReferences) && documentReferences.length > 0) return documentReferences;
    const v = field?.value;
    if (v && typeof v === 'object' && (v.filePath || v.fileName)) {
      return [{ id: v.id, fileName: v.fileName, filePath: v.filePath, isPrimary: true }];
    }
    return [];
  })();
  const hasDocument = displayFiles.length > 0;
  const fieldLabelText = field.label || field.title || field.name || 'Upload Document';
  const isMandatory = field.mandatory || field.required;

  // Accepted file types
  let acceptedType = 'image/*';
  const _fieldType = field?.type?.toLowerCase?.() || '';
  if (_fieldType === 'pdf') {
    acceptedType = 'application/pdf,image/*';
  } else if (_fieldType === 'document') {
    acceptedType = '.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,image/*';
  }

  return (
    <Box>
      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 500, color: 'text.primary' }}>
        {fieldLabelText}{isMandatory ? ' *' : ''}
      </Typography>

      {hasDocument ? (
        // Auto-filled / uploaded document(s) — clean row list matching the New Workflow Request form.
        <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, p: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {displayFiles.map((doc, i) => {
              const showPrimary = doc.isPrimary && displayFiles.length > 1;
              return (
                <Box key={doc.id || doc.documentId || doc.filePath || `doc-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon fontSize="small" sx={{ color: showPrimary ? 'warning.main' : 'action.active' }}>
                    {showPrimary ? 'star' : 'description'}
                  </Icon>
                  <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all' }}>
                    {doc.fileName || 'Document'}{showPrimary ? ' (Primary)' : ''}
                  </Typography>
                  {doc.filePath && (
                    <WDButton variant="outlined" size="small" onClick={() => handleViewDocument(doc)} sx={{ minWidth: '56px', height: '28px', fontSize: '0.75rem' }}>
                      VIEW
                    </WDButton>
                  )}
                </Box>
              );
            })}
            {isFileUploading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary' }}>Uploading…</Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
            <WDButton variant="text" size="small" component="label" disabled={isFileUploading} sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
              {allowMultiple ? 'Add / Replace' : 'Replace'}
              <input type="file" hidden multiple={allowMultiple} onChange={_onFileSelect} accept={acceptedType} ref={fileInputRef} />
            </WDButton>
            <WDButton variant="text" size="small" onClick={_onFileRemove} disabled={isFileUploading} sx={{ fontSize: '0.7rem', textTransform: 'none', color: 'error.main' }}>
              Remove
            </WDButton>
          </Box>
        </Box>
      ) : (
        // No document yet — upload control.
        <WDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={1.5}
          sx={{ border: 1, borderColor: 'grey.400', borderRadius: pxToRem(6), minHeight: pxToRem(45) }}
        >
          <WDButton variant="text" component="label" disabled={isFileUploading} sx={{ fontSize: '0.75rem', textTransform: 'none' }}>
            {isFileUploading ? 'Uploading…' : (file ? file.name : `Select ${field?.type || 'file'}`)}
            <input type="file" hidden multiple={allowMultiple} onChange={_onFileSelect} accept={acceptedType} ref={fileInputRef} />
          </WDButton>
          {isFileUploading && <CircularProgress size={16} />}
        </WDBox>
      )}

      {/* Display error message if any */}
      {error && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', color: 'error.main' }}>
          <Icon fontSize="small" color="error">error_outline</Icon>
          <Typography variant="caption" color="error" sx={{ ml: 0.5 }}>{error}</Typography>
        </Box>
      )}
    {/* Document Viewing Modal */}
    <Dialog 
      open={openModal} 
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          height: '80vh',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{documentTitle}</Typography>
        <IconButton onClick={handleCloseModal} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center',
        overflow: 'hidden',
        padding: 0
      }}>
        {isLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography>Loading document...</Typography>
          </Box>
        )}
        
        {viewError && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 2,
            p: 3,
            textAlign: 'center'
          }}>
            <Typography color="error" variant="h6">Error Loading Document</Typography>
            <Typography>{viewError}</Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleRetry}
            >
              Try Alternative Method
            </Button>
          </Box>
        )}
        
        {documentData && !isLoading && !viewError && (
          <Box sx={{ 
            width: '100%', 
            height: '100%', 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'auto'
          }}>
            <img 
              src={documentData} 
              alt={documentTitle}
              style={{ 
                maxWidth: '100%', 
                maxHeight: '100%', 
                objectFit: 'contain'
              }} 
            />
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleCloseModal}>Close</Button>
      </DialogActions>
    </Dialog>
  </Box>
  );
};

export default FileField;
